<template>
  <div>
    <h2>Authenticating...</h2>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import router from "@/router";
import store from "@/store";
import authClient from "@/api/oauth.api";
import { Role } from "@/utility/role";
import { jwtDecode } from "jwt-decode";

export default {
  setup() {
    const route = useRoute();
    const authCodeGrant = ref(route.query.code ? route.query.code : null);
    const verification = ref(route.query.state ? route.query.state : null);
    const tenantId = ref();
    const clientId = ref();
    const userRole = ref();
    const userGroups = ref();
    const token = ref();
    const accessToken = ref();
    const idToken = ref();
    const expiresIn = ref();

    onMounted(authenticate);

    async function authenticate() {
      if (!authCodeGrant.value) {
        router.push({
          name: "Error",
        });
        return;
      } else {
        token.value = await authClient.fetchToken(authCodeGrant.value);
        if (token.value?.data?.access_token) {
          accessToken.value = token.value?.data?.access_token;
          idToken.value = token.value?.data?.id_token
            ? jwtDecode(token.value.data.id_token)
            : null;
          expiresIn.value = token.value?.data?.expires_in;
          const accessTokenDecoded = jwtDecode(accessToken.value);
          userGroups.value = accessTokenDecoded["cognito:groups"];
        }
      }

      if (!accessToken.value) {
        router.push({
          name: "Error",
        });
        return;
      }

      if (!idToken.value) {
        router.push({
          name: "Error",
        });
        return;
      }

      if (!verification.value) {
        router.push({
          name: "Error",
        });
        return;
      }

      if (!expiresIn.value) {
        router.push({
          name: "Error",
        });
        return;
      }

      if (!userGroups.value) {
        router.push({
          name: "Error",
        });
        return;
      } else {
        userGroups.value.forEach((group) => {
          if (group === Role.SuperAdmin) {
            userRole.value = Role.SuperAdmin;
          } else if (group === Role.InternalAdmin) {
            userRole.value = Role.InternalAdmin;
          } else if (group === Role.ClientAdmin) {
            userRole.value = Role.ClientAdmin;
          } else if (group === Role.ClientUser) {
            userRole.value = Role.ClientUser;
          } else if (group === Role.ClientReadOnly) {
            userRole.value = Role.ClientReadOnly;
          }
        });

        const tenantClientGroupRegex =
          /T:{{0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}}{0,1}_{0,1}C:{{0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}}{0,1}/g;
        const tenantClientGroupMatch = tenantClientGroupRegex.exec(
          userGroups.value
        );

        if (tenantClientGroupMatch?.[0] != null) {
          const tenantClientSplitArray = tenantClientGroupMatch?.[0].split("_");
          const tenantRegex =
            /(?:T:)(({){0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(}){0,1})/g;
          const clientRegex =
            /(?:C:)(({){0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(}){0,1})/g;
          const tenantIdMatch = tenantRegex.exec(tenantClientSplitArray);
          const clientIdMatch = clientRegex.exec(tenantClientSplitArray);

          tenantId.value = tenantIdMatch?.[1] != null ? tenantIdMatch[1] : null;
          clientId.value = clientIdMatch?.[1] != null ? clientIdMatch[1] : null;
        }
      }

      if (!tenantId.value) {
        router.push({
          name: "Error",
        });
        return;
      }

      if (!clientId.value) {
        router.push({
          name: "Error",
        });
        return;
      }

      store.dispatch("authenticate", {
        accessToken: accessToken.value,
        given_name: idToken.value.given_name,
        family_name: idToken.value.family_name,
        emailAddress: idToken.value.email,
        userId: idToken.value.sub,
        userRole: userRole.value,
        verification: verification.value,
        tenantId: tenantId.value,
        clientId: clientId.value,
      });

      if (store.getters.isAuthenticated === true) {
        window.location.href = "/";
      } else {
        window.location.href = "/login";
      }
    }
  },
};
</script>
